import type { GraphQLFormattedError } from 'graphql';
import { ApplicationErrorCode } from '@oysterjs/core/graphql';
import { getApplicationError } from '@oysterjs/core/graphql/errors';
import { LoaderFunction } from 'react-router-dom';
import { client } from '~/api/client';
import { queryData } from '~/api/data/request';
import { loadQuery } from '~/api/data/responses';
import {
  GetAgencyEmailGuidelinesDocument,
  GetAgencyEmailGuidelinesQuery,
  GetAgencyEmailGuidelinesQueryVariables,
  GetEmailDraftTemplateDocument,
  GetEmailDraftTemplateQuery,
  GetEmailDraftTemplateQueryVariables,
  GetEmailTemplateChoicesDocument,
  GetEmailTemplateChoicesQuery,
  GetEmailTemplateChoicesQueryVariables,
  GetEmailTemplateDocument,
  GetEmailTemplateQuery,
  GetEmailTemplateQueryVariables,
  GetEmailTemplatesDocument,
  GetEmailTemplatesQuery,
  GetEmailTemplatesQueryVariables
} from '~/api/graphql';

export const getEmailTemplates = async () =>
  queryData<
    GetEmailTemplatesQuery,
    GetEmailTemplatesQueryVariables,
    GetEmailTemplatesQuery['emailTemplates']
  >(client, { query: GetEmailTemplatesDocument }, (data) => data?.emailTemplates);

const isUsageCountError = (error: GraphQLFormattedError) =>
  error.path?.[error.path.length - 1] === 'usageLast30Days';

// A usage count that fails comes back as null beside an error, and the page still lists the
// templates with usage marked unavailable. Any other error goes through the usual handling.
export const loadEmailTemplates: LoaderFunction = async () => {
  const result = await getEmailTemplates();
  const failedOnlyOnUsage =
    !!result.data && (result.error?.graphQLErrors ?? []).every(isUsageCountError);
  return failedOnlyOnUsage ? result.data : loadQuery(Promise.resolve(result));
};

/** An agency that never saved guidelines has no email configuration, which reads as none. */
export const getAgencyEmailGuidelines = async () =>
  queryData<GetAgencyEmailGuidelinesQuery, GetAgencyEmailGuidelinesQueryVariables, string[]>(
    client,
    { query: GetAgencyEmailGuidelinesDocument },
    (data) => data?.agency?.configuration.email?.guidelines ?? []
  );

export const loadAgencyEmailGuidelines: LoaderFunction = () =>
  loadQuery(getAgencyEmailGuidelines());

export const getEmailTemplate = (id: string) =>
  queryData<
    GetEmailTemplateQuery,
    GetEmailTemplateQueryVariables,
    GetEmailTemplateQuery['emailTemplate']
  >(client, { query: GetEmailTemplateDocument, variables: { id } }, (data) => data?.emailTemplate);

/**
 * A template that is missing, hidden from the user or archived loads as null, and the page
 * sends them back to the list. An archived template can't be changed, so it has no editor.
 */
export const loadEmailTemplate: LoaderFunction = async ({ params }) => {
  const result = await getEmailTemplate(params.templateId ?? '');
  const isNotFound = getApplicationError(result.error)?.code === ApplicationErrorCode.NotFound;
  if (isNotFound) return null;
  const template = await loadQuery(Promise.resolve(result));
  const isArchived = !(template instanceof Response) && !!template?.archivedAt;
  return isArchived ? null : template;
};

export type EmailTemplateChoice = GetEmailTemplateChoicesQuery['emailTemplates'][number];

export const getEmailTemplateChoices = () =>
  queryData<
    GetEmailTemplateChoicesQuery,
    GetEmailTemplateChoicesQueryVariables,
    GetEmailTemplateChoicesQuery['emailTemplates']
  >(client, { query: GetEmailTemplateChoicesDocument }, (data) => data?.emailTemplates);

export type EmailDraftTemplate = NonNullable<GetEmailDraftTemplateQuery['emailTemplate']>;

// The server answers a template the reader can't see like a missing one, and a reader
// without permission to read templates as unauthorized.
const hiddenTemplateCodes = [ApplicationErrorCode.NotFound, ApplicationErrorCode.Unauthorized];

/** The template an email was drafted from, or `hidden` when the reader can't read it. */
export const getEmailDraftTemplate = async (id: string): Promise<EmailDraftTemplate | 'hidden'> => {
  const result = await queryData<
    GetEmailDraftTemplateQuery,
    GetEmailDraftTemplateQueryVariables,
    GetEmailDraftTemplateQuery['emailTemplate']
  >(
    client,
    { query: GetEmailDraftTemplateDocument, variables: { id } },
    (data) => data?.emailTemplate
  );
  const errorCode = getApplicationError(result.error)?.code;
  const isHidden = !!errorCode && hiddenTemplateCodes.includes(errorCode);
  if (isHidden) return 'hidden';
  if (result.error) throw result.error;
  return result.data ?? 'hidden';
};
